import { IconType } from '@assembly-js/design-system';
import { useCallback } from 'react';

import { useAppDispatch } from 'src/hooks/useStore';
import { ApiTags } from 'src/services/api';
import {
  applicationSettingsApi,
  useGetAppQuery,
  useGetInstallsQuery,
  useUpdateAppMutation,
} from 'src/services/api/applicationsApi';
import {
  updateExtensionSettingsAction,
  updateModuleSettings,
} from 'src/store/settings/actions';

export const useUpdateAppSettings = (moduleId: string) => {
  const dispatch = useAppDispatch();
  const { data: { moduleSettings, extensionsSettings } = {}, isLoading } =
    useGetInstallsQuery('withFolders');

  const module = moduleSettings?.find((m) => m.id === moduleId);
  const extension = extensionsSettings?.[moduleId];
  const appId = extension?.appId;

  const { data: existingApp, isLoading: isAppLoading } = useGetAppQuery(
    { id: appId! },
    { skip: !appId },
  );
  const [updateAppMutation, { isLoading: isMutating }] = useUpdateAppMutation();

  const currentTitle = module?.label ?? '';
  const currentIcon = module?.icon;

  const updateSettings = useCallback(
    async ({ name, icon }: { name: string; icon: string }) => {
      if (!module) throw new Error('Module not found');

      if (module.type === 'module') {
        const patchResult = dispatch(
          applicationSettingsApi.util.updateQueryData(
            'getInstalls',
            'withFolders',
            (draft) => {
              const idx = draft.moduleSettings.findIndex(
                (m) => m.id === moduleId,
              );
              if (idx !== -1) {
                draft.moduleSettings[idx] = {
                  ...draft.moduleSettings[idx],
                  label: name,
                  icon,
                };
              }
            },
          ),
        );

        try {
          await dispatch(
            updateModuleSettings(
              (moduleSettings ?? []).map((m) =>
                m.id === moduleId ? { ...m, label: name, icon } : m,
              ),
              { disableSuccessAlert: true },
            ),
          );
        } catch (e) {
          patchResult.undo();
          throw e;
        }
      } else if (appId && existingApp) {
        const patchResult = dispatch(
          applicationSettingsApi.util.updateQueryData(
            'getInstalls',
            'withFolders',
            (draft) => {
              const idx = draft.moduleSettings.findIndex(
                (m) => m.id === moduleId,
              );
              if (idx !== -1) {
                draft.moduleSettings[idx] = {
                  ...draft.moduleSettings[idx],
                  label: name,
                  icon,
                };
              }
            },
          ),
        );

        try {
          await updateAppMutation({
            ...existingApp,
            name,
            icon: icon as IconType,
            moduleId,
          });
          dispatch(applicationSettingsApi.util.invalidateTags([ApiTags.apps]));
        } catch (e) {
          patchResult.undo();
          throw e;
        }
      } else {
        const patchResult = dispatch(
          applicationSettingsApi.util.updateQueryData(
            'getInstalls',
            'withFolders',
            (draft) => {
              const idx = draft.moduleSettings.findIndex(
                (m) => m.id === moduleId,
              );
              if (idx !== -1) {
                draft.moduleSettings[idx] = {
                  ...draft.moduleSettings[idx],
                  label: name,
                  icon,
                };
              }
              if (draft.extensionsSettings?.[moduleId]) {
                draft.extensionsSettings[moduleId] = {
                  ...draft.extensionsSettings[moduleId],
                  name,
                  icon: icon as IconType,
                };
              }
            },
          ),
        );

        try {
          if (extensionsSettings) {
            await dispatch(
              updateExtensionSettingsAction({
                ...extensionsSettings,
                [moduleId]: {
                  ...extensionsSettings[moduleId],
                  name,
                  icon: icon as IconType,
                },
              }),
            );
          }

          await dispatch(
            updateModuleSettings(
              (moduleSettings ?? []).map((m) =>
                m.id === moduleId ? { ...m, label: name, icon } : m,
              ),
              { disableSuccessAlert: true },
            ),
          );
        } catch (e) {
          patchResult.undo();
          throw e;
        }
      }
    },
    [
      module,
      moduleSettings,
      extensionsSettings,
      appId,
      existingApp,
      moduleId,
      dispatch,
      updateAppMutation,
    ],
  );

  return {
    updateSettings,
    isLoading: isLoading || isMutating || isAppLoading,
    currentTitle,
    currentIcon,
  };
};
